import { useState, useEffect, useRef } from 'react';
import { api } from '../../lib/api.js';
import { useIsMobile } from '../../lib/useIsMobile.js';

// The building blocks of Admin/Settings → Website: contact forms, newsletter
// sign-ups and website pop-ups share one shape —
//   list  → a compact row per item (name, status, one-line summary, numbers)
//   open  → a header, view tabs (Overview · data · Add to website · Edit)
//   edit  → collapsible sections, each with a one-line summary, a pinned
//           Save bar, and the LIVE PREVIEW beside them (below on a phone),
//           drawn by the same script the website runs.
//
// Mobile-first: rows and headers wrap, controls are ≥40px, the preview folds
// into a "Show preview" toggle on a phone.

export function ItemRow({ name, status, tone = 'live', summary, stats = [], badges = [], onOpen }) {
  return (
    <button type="button" onClick={onOpen} style={row}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 280px', minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{name}</span>
          <StatusPill status={status} tone={tone} />
          {badges.map((b) => <span key={b} style={pill}>{b}</span>)}
        </span>
        {summary && <span style={{ fontSize: 13, color: 'var(--muted)', overflowWrap: 'anywhere' }}>{summary}</span>}
      </span>
      <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--muted)' }}>
        {stats.map((s) => <span key={s.label}><b style={{ fontSize: 15, color: s.warn ? 'var(--error)' : 'var(--text, inherit)' }}>{s.value}</b> {s.label}</span>)}
      </span>
      <Chevron dir="right" />
    </button>
  );
}

export function StatusPill({ status, tone = 'live' }) {
  const t = TONES[tone] || TONES.muted;
  return <span style={{ ...pill, background: t.bg, color: t.fg, borderColor: 'transparent', fontWeight: 600 }}>{status}</span>;
}

// An open item: back link, name + status, quiet actions, then the view tabs.
export function Detail({ backLabel, onBack, name, status, tone, actions = [], tabs = [], tab, onTab, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <button type="button" onClick={onBack} style={backBtn}><Chevron dir="left" /> {backLabel}</button>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, overflowWrap: 'anywhere' }}>{name}</h3>
        {status && <StatusPill status={status} tone={tone} />}
        <span style={{ flex: 1 }} />
        {actions.map((a) => (
          <button key={a.label} type="button" disabled={a.disabled} onClick={a.onClick} style={{ ...quiet, color: a.danger ? 'var(--error)' : 'var(--muted)' }}>{a.label}</button>
        ))}
      </div>
      {tabs.length > 0 && (
        <div role="tablist" aria-label="Views" style={{ display: 'flex', gap: 2, overflowX: 'auto', borderBottom: '1px solid var(--hairline)' }}>
          {tabs.map(([id, label]) => {
            const on = tab === id;
            return (
              <button key={id} type="button" role="tab" aria-selected={on} onClick={() => onTab(id)}
                style={{ minHeight: 44, padding: '0 14px', border: 0, borderBottom: `2px solid ${on ? 'var(--brand, #e8455d)' : 'transparent'}`, background: 'none', color: on ? 'inherit' : 'var(--muted)', fontWeight: on ? 700 : 500, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {label}
              </button>
            );
          })}
        </div>
      )}
      {children}
    </div>
  );
}

// One collapsible part of an editor. Closed, it still says what's set.
export function Section({ title, summary, open, onToggle, children }) {
  return (
    <div style={{ border: `1px solid ${open ? 'var(--muted-line, #c9ccd3)' : 'var(--hairline)'}`, borderRadius: 12, background: 'var(--card)', overflow: 'hidden' }}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={sectionHead}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>{title}</span>
        <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}>{summary}</span>
        <Chevron dir={open ? 'up' : 'down'} />
      </button>
      {open && <div style={{ padding: '2px 14px 14px', borderTop: '1px solid var(--hairline)' }}>{children}</div>}
    </div>
  );
}
// A stack of sections; any number may be open at once.
export function Sections({ list, initial = [] }) {
  const [open, setOpen] = useState(() => new Set(initial));
  const flip = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {list.filter(Boolean).map((s) => (
        <Section key={s.id} title={s.title} summary={s.summary} open={open.has(s.id)} onToggle={() => flip(s.id)}>{s.content}</Section>
      ))}
    </div>
  );
}

export function Metrics({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
      {items.filter(Boolean).map((m) => (
        <div key={m.label} style={{ ...panelBox, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 24, fontWeight: 800, color: m.warn ? 'var(--error)' : m.good ? 'var(--success, #1a7f37)' : 'inherit' }}>{m.value}</span>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{m.label}</span>
          {m.hint && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{m.hint}</span>}
        </div>
      ))}
    </div>
  );
}

export function Glance({ rows, title = 'At a glance' }) {
  return (
    <div style={{ ...panelBox, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <b style={{ fontSize: 14 }}>{title}</b>
      <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, overflow: 'hidden' }}>
        {rows.filter(Boolean).map(([k, v], i) => (
          <div key={`${k}-${i}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', padding: '10px 12px', borderTop: i ? '1px solid var(--hairline)' : 0 }}>
            <span style={{ flex: '1 1 180px', fontSize: 13.5, fontWeight: 600 }}>{k}</span>
            <span style={{ flex: '0 1 auto', fontSize: 13, color: 'var(--muted)', overflowWrap: 'anywhere' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Editor + preview: side by side on a computer (the preview stays in view),
// stacked on a phone with the preview folded behind a toggle.
export function EditLayout({ editor, preview }) {
  const mobile = useIsMobile();
  const [show, setShow] = useState(false);
  if (mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button type="button" style={{ ...btn, alignSelf: 'flex-start' }} onClick={() => setShow((v) => !v)} aria-expanded={show}>{show ? 'Hide preview' : 'Show preview'}</button>
        {show && preview}
        {editor}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: '999 1 420px', minWidth: 0 }}>{editor}</div>
      <div style={{ flex: '1 1 340px', minWidth: 0, position: 'sticky', top: 12 }}>{preview}</div>
    </div>
  );
}

// The frame around a preview: Desktop / Phone, and an optional language.
export function PreviewPane({ children, langs = [], lang, onLang, note }) {
  const [device, setDevice] = useState('desktop');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)', flex: 1 }}>Live preview</span>
        {langs.length > 1 && (
          <select value={lang} onChange={(e) => onLang(e.target.value)} aria-label="Preview language" style={{ ...input, width: 'auto', minHeight: 34, padding: '4px 8px', fontSize: 12.5 }}>
            {langs.map(([code, nm]) => <option key={code} value={code}>{nm}</option>)}
          </select>
        )}
        {[['desktop', 'Desktop'], ['phone', 'Phone']].map(([d, l]) => (
          <button key={d} type="button" aria-pressed={device === d} onClick={() => setDevice(d)}
            style={{ minHeight: 34, padding: '0 12px', borderRadius: 999, border: `1px solid ${device === d ? 'transparent' : 'var(--hairline)'}`, background: device === d ? 'var(--text, #16181d)' : 'var(--bg)', color: device === d ? 'var(--bg, #fff)' : 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      <div style={{ background: '#ecebe8', borderRadius: 12, padding: device === 'phone' ? '14px 0' : 12, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: device === 'phone' ? 360 : 640, background: '#f7f6f3', borderRadius: 10, padding: 12, boxSizing: 'border-box', colorScheme: 'light' }}>
          {children}
        </div>
      </div>
      {note && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{note}</span>}
    </div>
  );
}

// Loads one of Pulse's public embed scripts into this page (once).
const loaded = {};
export function loadScript(src) {
  if (!loaded[src]) {
    loaded[src] = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src; s.onload = () => resolve(); s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return loaded[src];
}

// A contact or newsletter form drawn by its real web component from the form
// AS IT IS IN THE EDITOR (unsaved): the server shapes it exactly as the
// website would get it (POST …/preview), and the element paints that config.
// Submitting in the preview only shows the confirmation; nothing is sent.
export function FormPreview({ kind, entityId, apiScope, form, formId, lang }) {
  const ref = useRef(null);
  const [err, setErr] = useState('');
  const [ready, setReady] = useState(false);
  const tag = kind === 'contact' ? 'howler-contact' : 'howler-newsletter';
  useEffect(() => { loadScript(`/${tag}.js`).then(() => setReady(true)).catch(() => setErr('The preview couldn’t load.')); }, [tag]);
  const body = JSON.stringify(form);
  useEffect(() => {
    if (!ready) return undefined;
    let live = true;
    const t = setTimeout(() => {
      const call = kind === 'contact' ? api.contactPreview : api.newsletterPreview;
      call(entityId, apiScope, { form, formId, lang }).then((r) => {
        if (!live) return;
        if (r && r.error) { setErr(r.error); return; }
        setErr('');
        if (ref.current && r && r.config) ref.current.config = r.config;
      }).catch(() => live && setErr('The preview couldn’t load.'));
    }, 350);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, body, formId, lang, entityId, apiScope, kind]);
  const Tag = tag;
  return (
    <div>
      {err && <div style={{ fontSize: 12.5, color: 'var(--error)', marginBottom: 6 }}>{err}</div>}
      <Tag ref={ref} preview="" />
    </div>
  );
}

// A website pop-up drawn in place by howler-promo.js (its `inline` mode), from
// the pop-up as it is in the editor.
export function PromoPreview({ entityId, apiScope, promo, lang }) {
  const ref = useRef(null);
  const [err, setErr] = useState('');
  const [ready, setReady] = useState(false);
  useEffect(() => { loadScript('/howler-promo.js').then(() => setReady(true)).catch(() => setErr('The preview couldn’t load.')); }, []);
  const body = JSON.stringify(promo);
  useEffect(() => {
    if (!ready) return undefined;
    let live = true;
    const t = setTimeout(() => {
      api.sitePromoPreview(entityId, apiScope, promo, lang || '').then((r) => {
        if (!live) return;
        if (r && r.error) { setErr(r.error); return; }
        setErr('');
        if (ref.current && r && r.promo) ref.current.show(r.promo, { force: true, quiet: true });
      }).catch(() => live && setErr('The preview couldn’t load.'));
    }, 350);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, body, lang, entityId, apiScope]);
  return (
    <div>
      {err && <div style={{ fontSize: 12.5, color: 'var(--error)', marginBottom: 6 }}>{err}</div>}
      <howler-promo ref={ref} data-editor="" inline="" />
    </div>
  );
}

// Save / Cancel, pinned to the bottom while the sections scroll.
export function SaveBar({ onSave, onCancel, busy, isNew, createLabel = 'Create', error }) {
  return (
    <div style={{ position: 'sticky', bottom: 0, zIndex: 2, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '10px 12px', marginTop: 10, background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, boxShadow: '0 -4px 14px rgba(0,0,0,.06)' }}>
      <span style={{ flex: '1 1 200px', fontSize: 12.5, color: error ? 'var(--error)' : 'var(--muted)' }}>
        {error || (isNew ? 'Nothing is on your website until you create it.' : 'The preview shows unsaved changes. Save to put them on your website.')}
      </span>
      <button type="button" style={btn} onClick={onCancel}>Cancel</button>
      <button type="button" style={{ ...btn, ...primary }} disabled={busy} onClick={onSave}>{busy ? 'Saving…' : isNew ? createLabel : 'Save'}</button>
    </div>
  );
}

// ↑ ↓ for any ordered list: returns a moved copy.
export const moved = (list, i, d) => { const a = [...list]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

export function Chevron({ dir = 'right' }) {
  const rot = { right: 0, down: 90, left: 180, up: 270 }[dir];
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{ flex: 'none', transform: `rotate(${rot}deg)` }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export const when = (iso) => {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (!Number.isFinite(mins)) return '—';
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const TONES = {
  live: { bg: 'rgba(26,127,55,.12)', fg: 'var(--success, #1a7f37)' },
  paused: { bg: 'rgba(127,127,127,.14)', fg: 'var(--muted)' },
  scheduled: { bg: 'rgba(43,74,156,.12)', fg: '#2b4a9c' },
  muted: { bg: 'rgba(127,127,127,.14)', fg: 'var(--muted)' },
};
export const hint = { fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 8px' };
export const input = { width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '9px 10px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 14, fontFamily: 'inherit' };
export const btn = { minHeight: 40, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 13, cursor: 'pointer', boxSizing: 'border-box' };
export const primary = { background: 'var(--brand, #111)', color: '#fff', borderColor: 'transparent', fontWeight: 700 };
export const pill = { fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--bg)', border: '1px solid var(--hairline)', color: 'var(--muted)' };
export const panelBox = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '12px 14px' };
const row = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', width: '100%', padding: '14px 16px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', color: 'inherit', textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box', fontFamily: 'inherit' };
const backBtn = { alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 4, minHeight: 40, padding: '0 10px 0 4px', border: 0, background: 'none', color: 'inherit', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' };
const quiet = { minHeight: 40, padding: '0 10px', border: 0, background: 'none', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
const sectionHead = { width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px', minHeight: 52, padding: '10px 14px', border: 0, background: 'none', color: 'inherit', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' };
